import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Send, FileText, Loader2, User, Mail, Phone, Building2, BrainCircuit, Calendar } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { generateClaudeResponse } from '../lib/claude';
import { saveChatLead, extractContactInfo, ChatLeadData } from '../lib/saveChatLead';
import toast from 'react-hot-toast';

interface Message {
  role: 'user' | 'assistant' | 'system';
  content: string;
  files?: File[];
  options?: string[]; // Suggested quick replies
  slots?: { id: string; start: string; end: string }[];
}

interface AIChatTabProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

interface ContactFormData {
  full_name: string;
  email: string;
  phone: string;
  company: string;
}

const suggestedPrompts = [
  'chat.prompts.services',
  'chat.prompts.foundation',
  'chat.prompts.launch',
  'chat.prompts.contact'
];

const AIChatTab: React.FC<AIChatTabProps> = ({ messages, setMessages }) => {
  const { t, i18n } = useTranslation();
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  // Use a ref to track saving state across renders and prevent race conditions
  const isSavingRef = useRef(false);
  
  // Load emailSent state from session storage to persist across page reloads
  useEffect(() => {
    const savedEmailState = sessionStorage.getItem('chat_email_sent');
    if (savedEmailState === 'true') {
      setEmailSent(true);
    }
  }, []);

  // Update session storage when emailSent changes
  useEffect(() => {
    if (emailSent) {
      sessionStorage.setItem('chat_email_sent', 'true');
    }
  }, [emailSent]);
  const [availableSlots, setAvailableSlots] = useState<any[]>([]);
  const [contactForm, setContactForm] = useState<ContactFormData>({
    full_name: '',
    email: '',
    phone: '',
    company: ''
  });
  const [formErrors, setFormErrors] = useState<Partial<ContactFormData>>({});
  const chatRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Fetch available slots on mount
  useEffect(() => {
    fetch('/api/calendar/availability', { method: 'POST' })
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.slots)) {
          setAvailableSlots(data.slots);
        }
      })
      .catch(err => console.error('Failed to fetch availability', err));
  }, []);

  // Detect when assistant is asking for contact information
  useEffect(() => {
    const lastAssistantMessage = messages.filter(m => m.role === 'assistant').pop();
    if (lastAssistantMessage) {
      const content = lastAssistantMessage.content.toLowerCase();
      const askingForContact = 
        (content.includes('nome') || content.includes('chiami') || content.includes('name')) &&
        (content.includes('email') || content.includes('mail')) &&
        (content.includes('telefono') || content.includes('phone') || content.includes('numero')) &&
        (content.includes('azienda') || content.includes('company') || content.includes('società'));
      
      if (askingForContact && !showContactForm) {
        setShowContactForm(true);
      }
    }
  }, [messages, showContactForm]);

  // Validate form fields
  const validateForm = (): boolean => {
    const errors: Partial<ContactFormData> = {};
    let isValid = true;

    if (!contactForm.full_name.trim()) {
      errors.full_name = t('chat.form.nameRequired', 'Nome richiesto');
      isValid = false;
    }

    if (!contactForm.email.trim()) {
      errors.email = t('chat.form.emailRequired', 'Email richiesta');
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email)) {
      errors.email = t('chat.form.emailInvalid', 'Email non valida');
      isValid = false;
    }

    if (!contactForm.phone.trim()) {
      errors.phone = t('chat.form.phoneRequired', 'Telefono richiesto');
      isValid = false;
    } else if (!/^[\d\s\+\-\(\)]+$/.test(contactForm.phone)) {
      errors.phone = t('chat.form.phoneInvalid', 'Telefono non valido');
      isValid = false;
    }

    if (!contactForm.company.trim()) {
      errors.company = t('chat.form.companyRequired', 'Azienda richiesta');
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const isFormValid = (): boolean => {
    return !!(
      contactForm.full_name.trim() &&
      contactForm.email.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactForm.email) &&
      contactForm.phone.trim() &&
      /^[\d\s\+\-\(\)]+$/.test(contactForm.phone) &&
      contactForm.company.trim()
    );
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error(t('chat.form.fillAllFields', 'Compila tutti i campi correttamente'));
      return;
    }

    setIsLoading(true);
    
    try {
      // Save contact info
      const contactInfo: ChatLeadData = {
        full_name: contactForm.full_name.trim(),
        email: contactForm.email.trim().toLowerCase(),
        phone: contactForm.phone.trim(),
        company: contactForm.company.trim(),
        source: 'chat-lead'
      };

      // Get current language for email
      const currentLanguage = i18n.language || 'it';

      // Prepare message to send to assistant
      const contactMessage = `Nome: ${contactForm.full_name}\nEmail: ${contactForm.email}\nTelefono: ${contactForm.phone}\nAzienda: ${contactForm.company}`;
      
      const newMessage: Message = {
        role: 'user',
        content: contactMessage
      };

      const updatedMessages = [...messages, newMessage];
      setMessages(updatedMessages);

      // Reset form immediately (don't wait for save)
      setContactForm({
        full_name: '',
        email: '',
        phone: '',
        company: ''
      });
      setShowContactForm(false);
      setFormErrors({});

      // Try to save contact in background (non-blocking)
      // Chat flow continues regardless of save result
      saveChatLead({ ...contactInfo, language: currentLanguage, skipEmail: emailSent })
        .then(result => {
          if (result.success) {
            toast.success(t('chat.form.saved', 'Informazioni salvate!'));
            if (!emailSent) setEmailSent(true);
          } else {
            // Only show error if it's not a retryable network error
            if (!result.retryable) {
              toast.error(result.error || t('chat.form.saveError', 'Errore nel salvataggio'));
            } else {
              // For retryable errors (server down, timeout), show a warning but don't block
              console.warn('[AIChatTab] Contact save failed (retryable):', result.error);
              // Optionally show a subtle notification that it will be retried
              toast(t('chat.form.saveWarning', 'Le informazioni verranno salvate quando il server sarà disponibile'), {
                icon: 'ℹ️',
                duration: 3000,
              });
            }
          }
        })
        .catch(err => {
          console.error('[AIChatTab] Error saving contact:', err);
          // Don't show error to user - chat flow continues
        });

      // Continue with chat flow - get assistant response
      try {
        const response = await generateClaudeResponse(updatedMessages, availableSlots);
        if (response) {
          // Try to parse as JSON for structured response with options
          let messageContent = response;
          let suggestedOptions: string[] | undefined = undefined;
          let suggestedSlots: any[] | undefined = undefined;
          
          try {
            const parsed = JSON.parse(response);
            if (parsed.message) {
              messageContent = parsed.message;
              suggestedOptions = parsed.options;
              suggestedSlots = parsed.slots;
            }
          } catch {
            // Not JSON, use as plain text
          }

          // Strip square bracket tags from the visible message content
          if (messageContent) {
            messageContent = messageContent
              .replace(/\[ACTION:[^\]]*\]/gi, '')
              .replace(/\[APPOINTMENT_TIME:[^\]]*\]/gi, '')
              .trim();
          }
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: messageContent,
            options: suggestedOptions,
            slots: suggestedSlots
          }]);
        }
      } catch (chatError) {
        console.error('[AIChatTab] Error getting assistant response:', chatError);
        toast.error(t('chat.error', 'Errore nella risposta dell\'assistente'));
      }
    } catch (error) {
      console.error('[AIChatTab] Error submitting form:', error);
      toast.error(t('chat.form.submitError', 'Errore nell\'invio del form'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async (content: string) => {
    if (!content.trim()) return;

    const newMessage: Message = {
      role: 'user',
      content: content
    };

    const updatedMessages = [...messages, newMessage];
    setMessages(updatedMessages);
    setMessage('');
    setIsLoading(true);

    try {
      // Extract and save contact info if available (non-blocking)
      const contactInfo = extractContactInfo(updatedMessages);
      if ((contactInfo.email || contactInfo.full_name || contactInfo.phone) && !isSavingRef.current) {
        // Get current language for email
        const currentLanguage = i18n.language || 'it';
        
        // Mark as saving to prevent duplicate calls
        isSavingRef.current = true;
        
        // Save in background (don't block the chat)
        // Chat flow continues regardless of save result
        saveChatLead({ ...contactInfo, language: currentLanguage, skipEmail: emailSent })
          .then(result => {
            isSavingRef.current = false;
            if (result.success) {
              console.log('[AIChatTab] Contact saved:', contactInfo);
              if (result.emailSent) setEmailSent(true);
            } else {
              // Only log errors, don't show to user to avoid interrupting chat flow
              if (result.retryable) {
                console.warn('[AIChatTab] Contact save failed (retryable):', result.error);
              } else {
                console.error('[AIChatTab] Contact save failed (non-retryable):', result.error);
              }
            }
          })
          .catch(err => {
            isSavingRef.current = false;
            console.error('[AIChatTab] Error saving contact:', err);
            // Don't show error to user - chat flow continues
          });
      }

      // Continue with chat flow - get assistant response
      // This happens regardless of whether contact saving succeeded or failed
      try {
        const response = await generateClaudeResponse(updatedMessages, availableSlots);
        if (response) {
          // Try to parse as JSON for structured response with options
          let messageContent = response;
          let suggestedOptions: string[] | undefined = undefined;
          let suggestedSlots: any[] | undefined = undefined;
          
          try {
            const parsed = JSON.parse(response);
            if (parsed.message) {
              messageContent = parsed.message;
              suggestedOptions = parsed.options;
              suggestedSlots = parsed.slots;
            }
          } catch {
            // Not JSON, use as plain text
          }

          // Strip square bracket tags from the visible message content
          if (messageContent) {
            messageContent = messageContent
              .replace(/\[ACTION:[^\]]*\]/gi, '')
              .replace(/\[APPOINTMENT_TIME:[^\]]*\]/gi, '')
              .trim();
          }
          
          setMessages(prev => [...prev, {
            role: 'assistant',
            content: messageContent,
            options: suggestedOptions,
            slots: suggestedSlots
          }]);
        }
      } catch (chatError) {
        console.error('[AIChatTab] Error getting assistant response:', chatError);
        toast.error(t('chat.error', 'Errore nella risposta dell\'assistente'));
      }
    } catch (error) {
      console.error('[AIChatTab] Error sending message:', error);
      toast.error(t('chat.error', 'Errore nell\'invio del messaggio'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChatSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleSend(message);
  };

  const renderWelcomeMessage = () => {
    // Only show welcome message if there are no user messages
    if (messages.some(m => m.role === 'user')) return null;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="flex justify-start relative"
      >
        <div className="relative max-w-[90%] p-5 rounded-2xl bg-slate-900/80 border border-white/10 text-slate-200 rounded-tl-none backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
            <BrainCircuit size={12} className="text-cyan-500" />
            System Initialization
          </div>
          <div className="whitespace-pre-line leading-relaxed text-sm font-light">
            {t('chat.welcomeMessage')}
          </div>
        </div>
      </motion.div>
    );
  };
  
  const renderSuggestedPrompts = () => {
    if (messages.some(m => m.role === 'user')) return null;

    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
        className="flex flex-wrap gap-2 justify-start"
      >
        {suggestedPrompts.map(promptKey => (
          <button 
            key={promptKey}
            onClick={() => handleSend(t(promptKey))}
            className="px-3 py-1.5 bg-slate-900/50 border border-white/10 rounded-lg text-xs text-slate-400 hover:text-cyan-400 hover:border-cyan-500/30 hover:bg-cyan-950/30 transition-all font-mono"
          >
            {'>'} {t(promptKey)}
          </button>
        ))}
      </motion.div>
    )
  }

  // Render markdown content for assistant messages
  const renderMarkdown = (content: string) => {
    return (
      <ReactMarkdown
        components={{
          // Headings
          h1: ({ children }) => (
            <h1 className="text-lg font-bold text-white mt-4 mb-2 first:mt-0 font-mono uppercase tracking-wide">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-base font-bold text-white mt-4 mb-2 first:mt-0 flex items-center gap-2">
              <span className="text-cyan-500">#</span> {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-sm font-bold text-slate-200 mt-3 mb-2 first:mt-0">
              {children}
            </h3>
          ),
          // Custom renderers for action tags
          p: ({ children }) => {
            // Process children to find and style action tags
            const content = React.Children.toArray(children).map((child, idx) => {
              if (typeof child === 'string') {
                // Match [ACTION:...] and [APPOINTMENT_TIME:...] tags
                const parts = child.split(/(\[(?:ACTION|APPOINTMENT_TIME):[^\]]*\])/g);
                return parts.map((part, i) => {
                  const actionMatch = part.match(/^\[ACTION:([^\]]*)\]$/);
                  const timeMatch = part.match(/^\[APPOINTMENT_TIME:([^\]]*)\]$/);
                  
                  if (actionMatch) {
                    const action = actionMatch[1].trim();
                    let label = action;
                    let color = "bg-blue-500/20 text-blue-300 border-blue-500/30";
                    
                    if (action === 'GATHER_INFO') {
                      label = "Gathering Intelligence";
                      color = "bg-amber-500/20 text-amber-300 border-amber-500/30";
                    } else if (action === 'BOOK_MEETING') {
                      label = "Scheduling Phase";
                      color = "bg-purple-500/20 text-purple-300 border-purple-500/30";
                    } else if (action === 'CLOSE_DEAL') {
                      label = "Finalizing Agreement";
                      color = "bg-emerald-500/20 text-emerald-300 border-emerald-500/30";
                    }

                    return (
                      <span key={`${idx}-${i}`} className={`inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 rounded text-[10px] font-mono uppercase tracking-wider border ${color} select-none`}>
                        <BrainCircuit size={10} />
                        {label}
                      </span>
                    );
                  }
                  
                  if (timeMatch) {
                    const time = timeMatch[1].trim();
                    return (
                      <span key={`${idx}-${i}`} className="inline-flex items-center gap-1.5 px-2 py-0.5 mx-1 rounded text-[10px] font-mono uppercase tracking-wider border bg-cyan-500/20 text-cyan-300 border-cyan-500/30 select-none">
                        <Calendar size={10} />
                        {time.includes('T') ? new Date(time).toLocaleString() : time}
                      </span>
                    );
                  }
                  
                  return part;
                });
              }
              return child;
            });

            return (
              <p className="mb-3 last:mb-0 text-slate-300 leading-relaxed text-sm font-light">
                {content}
              </p>
            );
          },
          // Lists
          ul: ({ children }) => (
            <ul className="list-none mb-3 space-y-2 text-slate-300 text-sm ml-1">
              {children}
            </ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal list-inside mb-3 space-y-2 text-slate-300 text-sm ml-1">
              {children}
            </ol>
          ),
          li: ({ children }) => (
            <li className="flex gap-2 items-start">
              <span className="text-cyan-500/50 mt-1.5 text-[10px]">●</span>
              <span>{children}</span>
            </li>
          ),
          // Strong/Bold
          strong: ({ children }) => (
            <strong className="font-bold text-white">
              {children}
            </strong>
          ),
          // Emphasis/Italic
          em: ({ children }) => (
            <em className="italic text-slate-400">
              {children}
            </em>
          ),
          // Horizontal rule
          hr: () => (
            <hr className="my-4 border-white/10" />
          ),
          // Links
          a: ({ href, children }) => (
            <a
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              className="inline-flex items-center px-2 py-0.5 rounded text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 transition-colors border-b border-cyan-500/30 hover:border-cyan-500/60"
            >
              {children}
            </a>
          ),
          // Code
          code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const isInline = !match && !String(children).includes('\n');
            
            if (isInline) {
              return (
                <code className="px-1.5 py-0.5 rounded bg-black/30 border border-white/10 text-cyan-300 text-xs font-mono" {...props}>
                  {children}
                </code>
              );
            }
            return (
              <code className="block p-3 rounded-lg bg-black/30 border border-white/10 text-slate-300 text-xs font-mono overflow-x-auto mb-3" {...props}>
                {children}
              </code>
            );
          },
          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-cyan-500/50 pl-4 py-1 my-3 text-slate-400 italic text-sm bg-gradient-to-r from-cyan-500/5 to-transparent">
              {children}
            </blockquote>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };

  return (
    <>
      <div
        ref={chatRef}
        className="flex-1 overflow-y-auto p-5 space-y-6 bg-[#04060b] scroll-smooth"
        style={{ scrollbarWidth: 'thin', scrollbarColor: '#1e293b transparent' }}
      >
        {renderWelcomeMessage()}
        {renderSuggestedPrompts()}
        
        {messages.map((msg, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 * index }}
            className={`flex ${
              msg.role === 'user' ? 'justify-end' : 'justify-start'
            } relative group`}
          >
            {msg.role === 'assistant' && (
               <div className="absolute -left-3 top-0 w-1 h-full border-l border-white/5 opacity-0 group-hover:opacity-100 transition-opacity" />
            )}
            
            <div
              className={`relative max-w-[85%] p-5 rounded-2xl shadow-lg backdrop-blur-sm ${
                msg.role === 'user'
                  ? 'bg-cyan-500/10 text-white border border-cyan-500/20 rounded-tr-sm'
                  : 'bg-slate-900/80 text-slate-200 border border-white/10 rounded-tl-sm'
              }`}
            >
              {msg.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                  <BrainCircuit size={12} className="text-cyan-500" />
                  Cortex Analysis
                </div>
              )}
              
              {msg.role === 'assistant' ? (
                <>
                  <div className="prose prose-sm prose-invert max-w-none">
                    {renderMarkdown(msg.content)}
                  </div>
                  
                  {/* Suggested Slots */}
                  {msg.slots && msg.slots.length > 0 && (
                    <div className="mt-4 mb-2">
                      <p className="text-xs uppercase tracking-widest text-slate-500 mb-2 font-mono">Available Slots</p>
                      <div className="flex flex-wrap gap-2">
                        {msg.slots.map((slot, idx) => {
                          const date = new Date(slot.start);
                          const dateStr = date.toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' });
                          const timeStr = date.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });
                          
                          return (
                            <button
                              key={idx}
                              onClick={() => handleSend(`${t('chat.bookSlot', 'Prenota per')} ${dateStr} ${timeStr}`)}
                              className="flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-xs text-cyan-400 hover:bg-cyan-500/20 hover:border-cyan-500/50 transition-all font-mono group"
                            >
                              <Calendar className="w-3 h-3 group-hover:text-cyan-300" />
                              <span>{dateStr} <span className="text-slate-400">|</span> {timeStr}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Quick Replies */}
                  {msg.options && msg.options.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-white/5">
                      {msg.options.map((option, idx) => (
                        <button
                          key={idx}
                          onClick={() => handleSend(option)}
                          className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-xs text-cyan-400 hover:bg-cyan-500/10 hover:border-cyan-500/30 transition-all font-mono"
                        >
                          {option} &gt;
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className="whitespace-pre-line leading-relaxed text-sm font-light">
                  {msg.content}
                </div>
              )}
              
              {msg.files && msg.files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {msg.files.map((file, fileIndex) => (
                    <div
                      key={fileIndex}
                      className="flex items-center gap-3 p-2 bg-black/20 rounded border border-white/10"
                    >
                      <FileText className="w-4 h-4 text-cyan-400" />
                      <span className="text-xs truncate text-slate-300 font-mono">{file.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-start relative"
          >
            <div className="relative max-w-[90%] p-4 rounded-2xl bg-slate-900/50 border border-white/5 rounded-tl-sm">
              <div className="flex items-center gap-3">
                <div className="flex gap-1">
                  <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ repeat: Infinity, duration: 1 }} className="w-1 h-1 bg-cyan-500 rounded-full" />
                  <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ repeat: Infinity, duration: 1, delay: 0.2 }} className="w-1 h-1 bg-cyan-500 rounded-full" />
                  <motion.div animate={{ scale: [1, 1.5, 1] }} transition={{ repeat: Infinity, duration: 1, delay: 0.4 }} className="w-1 h-1 bg-cyan-500 rounded-full" />
                </div>
                <span className="text-xs font-mono text-slate-500 uppercase tracking-widest">Processing</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Contact Form */}
        {showContactForm && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-start relative"
          >
            <div className="relative max-w-[90%] w-full p-5 rounded-2xl bg-slate-900/90 border border-cyan-500/30 text-slate-200 shadow-lg shadow-cyan-500/5 backdrop-blur-md">
              <h3 className="text-sm font-bold mb-4 text-white uppercase tracking-wider font-mono flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-cyan-500 animate-pulse"></span>
                {t('chat.form.title', 'Data Input Required')}
              </h3>
              <form onSubmit={handleFormSubmit} className="space-y-4">
                {/* Nome */}
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                    {t('chat.form.name', 'Nome completo')} *
                  </label>
                  <div className="relative">
                    <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      value={contactForm.full_name}
                      onChange={(e) => {
                        setContactForm({ ...contactForm, full_name: e.target.value });
                        if (formErrors.full_name) setFormErrors({ ...formErrors, full_name: undefined });
                      }}
                      className={`w-full bg-black/40 border rounded-lg pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all font-light ${
                        formErrors.full_name ? 'border-red-500/50' : 'border-white/10'
                      }`}
                      placeholder={t('chat.form.namePlaceholder', 'Il tuo nome')}
                      disabled={isLoading}
                    />
                  </div>
                  {formErrors.full_name && (
                    <p className="text-[10px] text-red-400 mt-1 font-mono">{formErrors.full_name}</p>
                  )}
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                    {t('chat.form.email', 'Email')} *
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="email"
                      value={contactForm.email}
                      onChange={(e) => {
                        setContactForm({ ...contactForm, email: e.target.value });
                        if (formErrors.email) setFormErrors({ ...formErrors, email: undefined });
                      }}
                      className={`w-full bg-black/40 border rounded-lg pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all font-light ${
                        formErrors.email ? 'border-red-500/50' : 'border-white/10'
                      }`}
                      placeholder={t('chat.form.emailPlaceholder', 'tua@email.com')}
                      disabled={isLoading}
                    />
                  </div>
                  {formErrors.email && (
                    <p className="text-[10px] text-red-400 mt-1 font-mono">{formErrors.email}</p>
                  )}
                </div>

                {/* Telefono */}
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                    {t('chat.form.phone', 'Telefono')} *
                  </label>
                  <div className="relative">
                    <Phone className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="tel"
                      value={contactForm.phone}
                      onChange={(e) => {
                        setContactForm({ ...contactForm, phone: e.target.value });
                        if (formErrors.phone) setFormErrors({ ...formErrors, phone: undefined });
                      }}
                      className={`w-full bg-black/40 border rounded-lg pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all font-light ${
                        formErrors.phone ? 'border-red-500/50' : 'border-white/10'
                      }`}
                      placeholder={t('chat.form.phonePlaceholder', '+39 123 456 7890')}
                      disabled={isLoading}
                    />
                  </div>
                  {formErrors.phone && (
                    <p className="text-[10px] text-red-400 mt-1 font-mono">{formErrors.phone}</p>
                  )}
                </div>

                {/* Azienda */}
                <div>
                  <label className="block text-[10px] font-mono uppercase tracking-wider text-slate-500 mb-1.5">
                    {t('chat.form.company', 'Azienda')} *
                  </label>
                  <div className="relative">
                    <Building2 className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="text"
                      value={contactForm.company}
                      onChange={(e) => {
                        setContactForm({ ...contactForm, company: e.target.value });
                        if (formErrors.company) setFormErrors({ ...formErrors, company: undefined });
                      }}
                      className={`w-full bg-black/40 border rounded-lg pl-10 pr-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 transition-all font-light ${
                        formErrors.company ? 'border-red-500/50' : 'border-white/10'
                      }`}
                      placeholder={t('chat.form.companyPlaceholder', 'Nome della tua azienda')}
                      disabled={isLoading}
                    />
                  </div>
                  {formErrors.company && (
                    <p className="text-[10px] text-red-400 mt-1 font-mono">{formErrors.company}</p>
                  )}
                </div>

                {/* Submit Button */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={!isFormValid() || isLoading}
                    className="px-5 py-2.5 bg-cyan-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:bg-cyan-500 hover:shadow-[0_0_15px_rgba(6,182,212,0.4)] focus:outline-none flex items-center gap-2"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        UPLOADING...
                      </>
                    ) : (
                      <>
                        SUBMIT DATA
                        <Send className="w-3 h-3" />
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        )}
      </div>

      {!showContactForm && (
        <form
          onSubmit={handleChatSubmit}
          className="p-4 border-t border-white/10 bg-[#04060b]"
        >
          <div className="flex-1 relative">
            <div className="absolute inset-0 bg-white/5 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity" />
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleChatSubmit(e as any);
                }
              }}
              placeholder={t('chat.messagePlaceholder', 'Input command...')}
              className="w-full bg-black/40 border border-white/10 rounded-xl pl-4 pr-14 py-4 text-white resize-none focus:outline-none focus:border-cyan-500/50 focus:bg-black/60 placeholder-slate-600 font-light text-sm transition-all"
              disabled={isLoading}
              rows={1}
              style={{ lineHeight: '1.5rem', maxHeight: '9rem' }}
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2.5 bg-cyan-500/10 border border-cyan-500/20 rounded-lg text-cyan-400 transition-all disabled:opacity-30 disabled:pointer-events-none hover:bg-cyan-500 hover:text-black hover:shadow-[0_0_15px_rgba(6,182,212,0.4)]"
              disabled={!message.trim() || isLoading}
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export default AIChatTab; 